import React, { useEffect, useRef } from 'react';
import { useIntersection } from 'react-use';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import { outputIfNoVariables } from '../../../utilities/misc';
import { rem } from '../../../utilities/style';
import { colors } from '../../../theme/colors';
import { media } from '../../../theme/media';
import { headers, body, richTextStyles } from '../../../styles/type';

import Button from '../../buttons/Button';
import ButtonGroup from '../../ButtonGroup';
import ButtonText from '../../buttons/ButtonText';
import Image from '../../Image';
import TextRenderer from '../../TextRenderer';

const Card = styled.div`

  ${ButtonGroup} {
    margin-top: auto;
  }
`;

const StyledImage = styled(Image)`
  display: block;
  width: 100%;
  margin-bottom: ${rem(24)};
  border-radius: ${rem(8)};

  ${media('>=mini')} {
    display: none;
  }
`;

const Subhead = styled.p`
  ${headers.eyebrow}
  margin-bottom: ${rem(12)};
  padding-top: ${rem(8)};
  font-size: ${rem(14)};
`;

const Headline = styled.h3`
  ${headers.h4}
  color: ${colors.blue['800']};
  margin-bottom: ${rem(20)};
`;

const Text = styled.div`
  ${body.regular}
  ${richTextStyles}
  margin-bottom: ${rem(20)};
  color: ${colors.gray['600']};
`;

const ScrollSyncItem = ({
  image,
  subhead,
  headline,
  text,
  primaryCtaText,
  primaryCtaLink,
  secondaryCtaText,
  secondaryCtaLink,
  index,
  indexIntersectCallback,
}) => {
  const intersectionRef = useRef(null);
  const intersection = useIntersection(intersectionRef, {
    rootMargin: '-25% 0% -75% 0%',
    threshold: 0,
  });

  useEffect(() => {
    if (intersection?.isIntersecting && indexIntersectCallback) {
      indexIntersectCallback(index);
    }
  }, [intersection, indexIntersectCallback, index]);

  return (
    <Card ref={intersectionRef}>
      {image && (
        <StyledImage
          image={image}
          alt=""
        />
      )}
      {subhead && (
        <Subhead>
          <TextRenderer text={subhead} />
        </Subhead>
      )}
      {headline && (
        <Headline>
          <TextRenderer text={headline} />
        </Headline>
      )}
      {text && (
        <Text>
          <TextRenderer text={text} richText />
        </Text>
      )}

      <ButtonGroup>
        {primaryCtaText && primaryCtaLink && (
          <Button
            to={primaryCtaLink}
            aria-label={outputIfNoVariables(`${headline} — ${primaryCtaText}`)}
          >
            <TextRenderer text={primaryCtaText} />
          </Button>
        )}
        {secondaryCtaText && secondaryCtaLink && (
          <ButtonText
            to={secondaryCtaLink}
            aria-label={outputIfNoVariables(`${headline} — ${secondaryCtaText}`)}
            onClick={(event) => event.stopPropagation()}
          >
            <TextRenderer text={secondaryCtaText} />
          </ButtonText>
        )}
      </ButtonGroup>
    </Card>
  );
};

ScrollSyncItem.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  image: PropTypes.object,
  subhead: PropTypes.string,
  headline: PropTypes.string,
  text: PropTypes.shape({
    raw: PropTypes.string
  }),
  primaryCtaText: PropTypes.string,
  primaryCtaLink: PropTypes.string,
  secondaryCtaText: PropTypes.string,
  secondaryCtaLink: PropTypes.string,
};

export default ScrollSyncItem;
